import * as React from 'react'
import * as ProgressPrimitive from '@radix-ui/react-progress'
import { AlertTriangle, CheckCircle, AlertCircle } from 'lucide-react'
import { Badge, CapacityStatusBadge, DatabaseProductBadge } from './badge'
import {
  getCapacityStatus,
  getCapacityStatusClasses,
  getCapacityProgressClasses,
  getCapacityStatusIcon,
  formatCapacityArray,
  getCapacityRecommendations,
  type CapacityStatus,
} from '@/lib/capacity-design-system'
import { cn } from '@/lib/utils'
import { type Compartment } from './input'

function Progress({
  className,
  value,
  ...props
}: React.ComponentProps<typeof ProgressPrimitive.Root>) {
  return (
    <ProgressPrimitive.Root
      data-slot="progress"
      className={cn(
        'bg-primary/20 relative h-2 w-full overflow-hidden rounded-full',
        className
      )}
      {...props}
    >
      <ProgressPrimitive.Indicator
        data-slot="progress-indicator"
        className="bg-primary h-full w-full flex-1 transition-all"
        style={{ transform: `translateX(-${100 - (value || 0)}%)` }}
      />
    </ProgressPrimitive.Root>
  )
}

// Capacity Utilization Display Component

interface CapacityUtilizationDisplayProps {
  vehicleCapacity: number[]
  usedCapacity: number[]
  compartments?: Compartment[]
  compact?: boolean
  showRecommendations?: boolean
  thresholds?: {
    warning: number
    critical: number
  }
}

const CapacityUtilizationDisplay = ({
  vehicleCapacity,
  usedCapacity,
  compartments,
  compact = false,
  showRecommendations = true,
  thresholds = { warning: 80, critical: 95 },
}: CapacityUtilizationDisplayProps) => {
  const calculateUtilization = (used: number, total: number) => {
    if (total === 0) return 0
    return Math.min(100, (used / total) * 100)
  }

  // Calculate utilization for each dimension
  const dimensions = [
    { name: 'Weight', unit: 'kg', index: 0 },
    { name: 'Volume', unit: 'm³', index: 1 },
    { name: 'Count', unit: 'items', index: 2 },
  ]

  const utilisationData = dimensions.map(dim => {
    const used = usedCapacity[dim.index] || 0
    const total = vehicleCapacity[dim.index] || 0
    const percentage = calculateUtilization(used, total)
    const status = getCapacityStatus(percentage, thresholds)

    return {
      ...dim,
      used,
      total,
      percentage,
      status,
    }
  })

  const overallPercentage = Math.max(...utilisationData.map(d => d.percentage))
  const overallStatus = getCapacityStatus(overallPercentage, thresholds)
  const isOverCapacity = utilisationData.some(d => d.percentage > 100)

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        {getCapacityStatusIcon(overallStatus)}
        <div className="flex-1 min-w-0">
          <div className="capacity-progress">
            <div
              className={cn('capacity-progress-fill', overallStatus)}
              style={{ width: `${Math.min(100, overallPercentage)}%` }}
            />
          </div>
        </div>
        <CapacityStatusBadge
          status={overallStatus}
          percentage={overallPercentage}
          compact
        />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Overall Status */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {getCapacityStatusIcon(overallStatus)}
          <span className="font-semibold">Capacity Utilization</span>
        </div>
        <div className="flex items-center gap-2">
          <CapacityStatusBadge
            status={overallStatus}
            percentage={overallPercentage}
          />
          {isOverCapacity && <Badge variant="destructive">Over Capacity</Badge>}
        </div>
      </div>

      {/* Detailed Breakdown */}
      <div className="space-y-3">
        {utilisationData.map(item => (
          <div key={item.name} className="space-y-1">
            <div className="flex items-center justify-between text-sm">
              <span className="font-semibold">{item.name}</span>
              <div className="flex items-center gap-2">
                <span className={getCapacityStatusClasses(item.status)}>
                  {item.used.toFixed(2)} / {item.total.toFixed(2)} {item.unit}
                </span>
                <CapacityStatusBadge
                  status={item.status}
                  percentage={item.percentage}
                  compact
                />
              </div>
            </div>
            <div className="capacity-progress">
              <div
                className={cn('capacity-progress-fill', item.status)}
                style={{ width: `${Math.min(100, item.percentage)}%` }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Compartment Breakdown */}
      {!compact && compartments && compartments.length > 0 && (
        <div className="mt-4 pt-4 border-t">
          <div className="text-sm font-semibold mb-2">Compartment Breakdown</div>
          <div className="space-y-2">
            {compartments.map(compartment => (
              <div
                key={compartment.id}
                className="flex items-center justify-between text-sm"
              >
                <div className="flex items-center gap-2">
                  <span>{compartment.name}</span>
                  <Badge variant="outline" className="text-sm">
                    {compartment.type}
                  </Badge>
                </div>
                <Badge variant="outline">
                  {compartment.capacity[0]} kg / {compartment.capacity[1]} m³ /{' '}
                  {compartment.capacity[2]} items
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recommendations */}
      {showRecommendations &&
        (overallStatus === 'warning' ||
          overallStatus === 'critical' ||
          overallStatus === 'overflow') && (
          <div className="mt-4 p-3 bg-muted/50 rounded-lg">
            <div className="text-sm font-semibold mb-2 flex items-center gap-2">
              {getCapacityStatusIcon(overallStatus)}
              Recommendations
            </div>
            <div className="space-y-1">
              {getCapacityRecommendations(overallStatus, overallPercentage).map(
                (recommendation, index) => (
                  <div key={index} className="text-sm text-muted-foreground">
                    {recommendation}
                  </div>
                )
              )}
            </div>
          </div>
        )}
    </div>
  )
}

export {
  Progress,
  CapacityUtilizationDisplay,
  type CapacityUtilizationDisplayProps,
}
